import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  Add,
  Autorenew,
  Chat,
  CheckCircleOutline,
  DeleteOutline,
  Edit,
  HelpOutline,
  Hub,
  LinkOff,
  PowerSettingsNew,
  QrCode2,
  Refresh,
  RestartAlt,
  Wifi,
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import DetailHeader from '../components/DetailHeader';
import { useConfirmDialog } from '../components/ConfirmDialog';
import useContactZConnectionsWebSocket, { type ContactZConnectionObserverEvent } from '../hooks/useContactZConnectionsWebSocket';
import usePageTitle from '../hooks/usePageTitle';
import { useNumberFormat } from '../hooks/useNumberFormat';
import {
  bootstrapContactZIntegration,
  connectContactZConnection,
  createContactZConnection,
  deleteContactZConnection,
  disconnectContactZConnection,
  fetchContactZBootstrap,
  manualBindContactZIntegration,
  restartContactZConnection,
  syncContactZConnectionState,
  testContactZWebhook,
  updateContactZConnection,
} from '../services/contactZ';
import {
  CONTACT_Z_CONNECTION_STATUS_SEVERITY,
  canConnectContactZConnection,
  canDisconnectContactZConnection,
} from '../utils/contactZ';
import { formatDateTime } from '../utils/date';
import { formatPhone } from '../utils/phone';
import contactZIcon from '../assets/integrations/contact-z/contact-z-icon.svg';

import type {
  ContactZBootstrapPayload,
  ContactZConnection,
  ContactZConnectionStatus,
  ContactZIntegrationBinding,
  ContactZManualBindResult,
} from '../types';

interface ConnectionDialogState {
  mode: 'create' | 'edit';
  open: boolean;
  connectionId: string | null;
  name: string;
}

interface ManualBindDialogState {
  open: boolean;
  organizationUuid: string;
  integrationConfigUuid: string;
  apiKey: string;
  forceReplace: boolean;
  importExistingConnections: boolean;
}

function buildPageErrorMessage(error: unknown, fallback: string): string {
  const maybeAxiosError = error as {
    response?: { data?: { error?: string; detail?: string } };
    message?: string;
  };
  return (
    maybeAxiosError?.response?.data?.error
    || maybeAxiosError?.response?.data?.detail
    || maybeAxiosError?.message
    || fallback
  );
}

function ContactZStatusChip({
  status,
  label,
}: {
  status: ContactZConnectionStatus;
  label: string;
}) {
  const theme = useTheme();
  const severity = CONTACT_Z_CONNECTION_STATUS_SEVERITY[status];
  const colorMap = {
    default: theme.palette.text.secondary,
    success: theme.palette.success.main,
    warning: theme.palette.warning.main,
    error: theme.palette.error.main,
  } as const;

  return (
    <Chip
      label={label}
      size="small"
      variant="outlined"
      sx={{
        bgcolor: alpha(colorMap[severity], 0.12),
        borderColor: alpha(colorMap[severity], 0.3),
        color: colorMap[severity],
      }}
    />
  );
}

function getQrPlaceholderCopy(
  t: (key: string, options?: Record<string, unknown>) => string,
  status: ContactZConnectionStatus,
): {
  icon: typeof LinkOff;
  iconColor?: string;
  iconSize?: number;
  message: string;
} {
  if (status === 'connected') {
    return {
      icon: CheckCircleOutline,
      iconColor: 'success.main',
      iconSize: 96,
      message: t('pages:integrations.contactZ.connections.qr.connected'),
    };
  }

  return {
    icon: LinkOff,
    message: t('pages:integrations.contactZ.connections.qr.empty'),
  };
}

export default function ContactZPage() {
  const { t } = useTranslation(['pages', 'common']);
  const { locale, formatNumber } = useNumberFormat();
  const { confirm, DialogComponent } = useConfirmDialog();
  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down('sm'));

  const [bootstrapData, setBootstrapData] = useState<ContactZBootstrapPayload | null>(null);
  const [integration, setIntegration] = useState<ContactZIntegrationBinding | null>(null);
  const [connections, setConnections] = useState<ContactZConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [testWebhookMessage, setTestWebhookMessage] = useState('ping');
  const [dialogState, setDialogState] = useState<ConnectionDialogState>({
    mode: 'create',
    open: false,
    connectionId: null,
    name: '',
  });
  const [manualBindDialog, setManualBindDialog] = useState<ManualBindDialogState>({
    open: false,
    organizationUuid: '',
    integrationConfigUuid: '',
    apiKey: '',
    forceReplace: false,
    importExistingConnections: true,
  });

  const canCreateConnections = bootstrapData?.permissions.canCreateConnections ?? false;
  const canUpdateConnections = bootstrapData?.permissions.canUpdateConnections ?? false;
  const canDeleteConnections = bootstrapData?.permissions.canDeleteConnections ?? false;
  const canControlConnections = bootstrapData?.permissions.canControlConnections ?? false;
  const canReadConnections = bootstrapData?.permissions.canReadConnections ?? false;
  const canViewIntegrationBinding = bootstrapData?.permissions.canViewBinding ?? false;
  const canManualBind = bootstrapData?.permissions.canManualBind ?? false;
  const showIntegrationBox = canViewIntegrationBinding || canManualBind;

  usePageTitle(t('pages:integrations.contactZ.title'));

  const applyConnectionEvent = useCallback(
    (
      event: ContactZConnectionObserverEvent,
      action: string,
    ) => {
      setConnections((previous) => {
        const shouldRemove = action === 'delete' || event.isActive === false;
        if (shouldRemove) {
          return previous.filter((connection) => connection.id !== event.id);
        }

        const nextConnection = event as ContactZConnection;
        const existingIndex = previous.findIndex((connection) => connection.id === nextConnection.id);
        if (existingIndex === -1) {
          return [...previous, nextConnection].sort((left, right) => {
            const nameComparison = left.name.localeCompare(right.name, locale, { sensitivity: 'base' });
            if (nameComparison !== 0) {
              return nameComparison;
            }
            return left.createdAt.localeCompare(right.createdAt);
          });
        }

        return previous.map((connection) => (
          connection.id === nextConnection.id ? { ...connection, ...nextConnection } : connection
        ));
      });
    },
    [locale],
  );

  const loadPage = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const nextBootstrap = await fetchContactZBootstrap();
      setBootstrapData(nextBootstrap);
      setIntegration(nextBootstrap.integration.binding);
      setConnections(Array.isArray(nextBootstrap.connections.items) ? nextBootstrap.connections.items : []);
    } catch (loadError) {
      console.error('Failed to load Contact Z page:', loadError);
      setError(buildPageErrorMessage(loadError, t('pages:integrations.contactZ.feedback.loadError')));
      setBootstrapData(null);
      setIntegration(null);
      setConnections([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void loadPage();
  }, [loadPage]);

  useContactZConnectionsWebSocket({
    enabled: canReadConnections,
    onEvent: applyConnectionEvent,
  });

  const handleBootstrap = useCallback(async () => {
    try {
      setBusyAction('bootstrap');
      await bootstrapContactZIntegration();
      await loadPage();
      setSuccessMessage(t('pages:integrations.contactZ.feedback.bootstrapSuccess'));
      setError(null);
    } catch (bootstrapError) {
      console.error('Failed to bootstrap Contact Z integration:', bootstrapError);
      setError(buildPageErrorMessage(bootstrapError, t('pages:integrations.contactZ.feedback.bootstrapError')));
    } finally {
      setBusyAction(null);
    }
  }, [loadPage, t]);

  const handleTestWebhook = useCallback(async () => {
    try {
      setBusyAction('test-webhook');
      await testContactZWebhook(testWebhookMessage.trim() || 'ping');
      setSuccessMessage(t('pages:integrations.contactZ.feedback.testWebhookSuccess'));
      setError(null);
    } catch (testError) {
      console.error('Failed to test Contact Z webhook:', testError);
      setError(buildPageErrorMessage(testError, t('pages:integrations.contactZ.feedback.testWebhookError')));
    } finally {
      setBusyAction(null);
    }
  }, [t, testWebhookMessage]);

  const handleOpenCreateDialog = () => {
    setDialogState({
      mode: 'create',
      open: true,
      connectionId: null,
      name: '',
    });
  };

  const handleOpenEditDialog = (connection: ContactZConnection) => {
    setDialogState({
      mode: 'edit',
      open: true,
      connectionId: connection.id,
      name: connection.name,
    });
  };

  const handleCloseDialog = useCallback(() => {
    setDialogState((previous) => ({ ...previous, open: false }));
  }, []);

  const handleOpenManualBindDialog = useCallback(() => {
    setManualBindDialog({
      open: true,
      organizationUuid: integration?.organizationUuid ?? '',
      integrationConfigUuid: integration?.integrationConfigUuid ?? '',
      apiKey: '',
      forceReplace: false,
      importExistingConnections: true,
    });
  }, [integration]);

  const handleCloseManualBindDialog = useCallback(() => {
    setManualBindDialog((previous) => ({ ...previous, open: false }));
  }, []);

  const buildManualBindSuccessMessage = useCallback((result: ContactZManualBindResult) => {
    const messageKey = result.replacedExistingBinding
      ? 'pages:integrations.contactZ.feedback.manualBindReplaceSuccess'
      : 'pages:integrations.contactZ.feedback.manualBindSuccess';
    return t(messageKey, {
      created: formatNumber(result.importedConnections.created),
      updated: formatNumber(result.importedConnections.updated),
      reactivated: formatNumber(result.importedConnections.reactivated),
      deactivated: formatNumber(result.importedConnections.deactivated),
    });
  }, [formatNumber, t]);

  const handleSaveDialog = useCallback(async () => {
    const trimmedName = dialogState.name.trim();
    if (!trimmedName) {
      setError(t('pages:integrations.contactZ.feedback.connectionNameRequired'));
      return;
    }

    try {
      setBusyAction(dialogState.mode === 'create' ? 'create-connection' : `update-${dialogState.connectionId}`);
      if (dialogState.mode === 'create') {
        await createContactZConnection(trimmedName);
        await loadPage();
        setSuccessMessage(t('pages:integrations.contactZ.feedback.connectionCreateSuccess'));
      } else if (dialogState.connectionId) {
        await updateContactZConnection(dialogState.connectionId, trimmedName);
        await loadPage();
        setSuccessMessage(t('pages:integrations.contactZ.feedback.connectionUpdateSuccess'));
      }
      setError(null);
      handleCloseDialog();
    } catch (saveError) {
      console.error('Failed to save Contact Z connection:', saveError);
      setError(buildPageErrorMessage(saveError, t('pages:integrations.contactZ.feedback.connectionSaveError')));
    } finally {
      setBusyAction(null);
    }
  }, [dialogState, handleCloseDialog, loadPage, t]);

  const handleSaveManualBindDialog = useCallback(async () => {
    const organizationUuid = manualBindDialog.organizationUuid.trim();
    const integrationConfigUuid = manualBindDialog.integrationConfigUuid.trim();
    const apiKey = manualBindDialog.apiKey.trim();

    if (!organizationUuid || !integrationConfigUuid || !apiKey) {
      setError(t('pages:integrations.contactZ.feedback.manualBindFieldsRequired'));
      return;
    }

    try {
      setBusyAction('manual-bind');
      const result = await manualBindContactZIntegration({
        organizationUuid,
        integrationConfigUuid,
        apiKey,
        forceReplace: manualBindDialog.forceReplace,
        importExistingConnections: manualBindDialog.importExistingConnections,
      });
      await loadPage();
      setSuccessMessage(buildManualBindSuccessMessage(result));
      setError(null);
      handleCloseManualBindDialog();
    } catch (manualBindError) {
      console.error('Failed to manually bind Contact Z integration:', manualBindError);
      setError(buildPageErrorMessage(manualBindError, t('pages:integrations.contactZ.feedback.manualBindError')));
    } finally {
      setBusyAction(null);
    }
  }, [buildManualBindSuccessMessage, handleCloseManualBindDialog, loadPage, manualBindDialog, t]);

  const handleDeleteConnection = useCallback(async (connection: ContactZConnection) => {
    const confirmed = await confirm({
      title: t('pages:integrations.contactZ.deleteConfirm.title'),
      message: t('pages:integrations.contactZ.deleteConfirm.message', { name: connection.name }),
      type: 'warning',
      icon: <DeleteOutline color="error" />,
      confirmIcon: <DeleteOutline />,
      confirmText: t('common:actions.delete'),
    });

    if (!confirmed) {
      return;
    }

    try {
      setBusyAction(`delete-${connection.id}`);
      await deleteContactZConnection(connection.id);
      await loadPage();
      setSuccessMessage(t('pages:integrations.contactZ.feedback.connectionDeleteSuccess'));
      setError(null);
    } catch (deleteError) {
      console.error('Failed to delete Contact Z connection:', deleteError);
      setError(buildPageErrorMessage(deleteError, t('pages:integrations.contactZ.feedback.connectionDeleteError')));
    } finally {
      setBusyAction(null);
    }
  }, [confirm, loadPage, t]);

  const handleConnectionAction = useCallback(async (
    connection: ContactZConnection,
    action: 'sync' | 'connect' | 'disconnect' | 'restart',
  ) => {
    try {
      setBusyAction(`${action}-${connection.id}`);
      let nextConnection: ContactZConnection;
      if (action === 'sync') {
        nextConnection = await syncContactZConnectionState(connection.id);
      } else if (action === 'connect') {
        nextConnection = await connectContactZConnection(connection.id);
      } else if (action === 'disconnect') {
        nextConnection = await disconnectContactZConnection(connection.id);
      } else {
        nextConnection = await restartContactZConnection(connection.id);
      }
      applyConnectionEvent(nextConnection, 'update');
      setBootstrapData((previous) => {
        if (!previous) {
          return previous;
        }
        return {
          ...previous,
          connections: {
            ...previous.connections,
            items: previous.connections.items.map((item) => (item.id === nextConnection.id ? nextConnection : item)),
          },
        };
      });
      setSuccessMessage(t(`pages:integrations.contactZ.feedback.actions.${action}`));
      setError(null);
    } catch (actionError) {
      console.error(`Failed Contact Z ${action} action:`, actionError);
      setError(buildPageErrorMessage(actionError, t('pages:integrations.contactZ.feedback.actionError')));
    } finally {
      setBusyAction(null);
    }
  }, [applyConnectionEvent, t]);

  const breadcrumbs = [
    { label: t('common:menu.integrations'), to: '/integrations' },
    { label: t('pages:integrations.contactZ.title') },
  ];

  const availabilitySummary = useMemo(() => {
    const accountSummary = bootstrapData?.entitlements.accounts;
    if (!accountSummary) {
      return t('pages:integrations.contactZ.availability.unavailable');
    }
    if (accountSummary.remaining == null || accountSummary.limit == null) {
      return t('pages:integrations.contactZ.availability.unlimited');
    }
    return t('pages:integrations.contactZ.availability.summary', {
      available: formatNumber(accountSummary.remaining),
      total: formatNumber(accountSummary.limit),
    });
  }, [bootstrapData, formatNumber, t]);

  const headerActions = (
    <>
      <Button
        startIcon={busyAction === 'refresh' ? <CircularProgress size={16} color="inherit" /> : <Refresh />}
        variant="outlined"
        onClick={async () => {
          try {
            setBusyAction('refresh');
            await loadPage();
            setSuccessMessage(null);
          } finally {
            setBusyAction(null);
          }
        }}
      >
        {t('common:actions.refresh')}
      </Button>
      {!integration && (
        <Button
          startIcon={busyAction === 'bootstrap' ? <CircularProgress size={16} color="inherit" /> : <Hub />}
          variant="contained"
          onClick={handleBootstrap}
        >
          {t('pages:integrations.contactZ.actions.bootstrap')}
        </Button>
      )}
    </>
  );

  return (
    <Box>
      <DetailHeader
        title={t('pages:integrations.contactZ.title')}
        subtitle={t('pages:integrations.contactZ.subtitle')}
        breadcrumbs={breadcrumbs}
        backHref="/integrations"
        actions={headerActions}
        sectionIcon={(
          <Box
            component="img"
            src={contactZIcon}
            alt="Contact Z"
            sx={{ height: 32, width: 32 }}
          />
        )}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <Card
            sx={{
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
            }}
          >
            <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
                <Hub sx={{ color: 'primary.main' }} />
                <Typography variant="h6">
                  {t('pages:integrations.contactZ.availability.title')}
                </Typography>
              </Box>
              {loading ? (
                <>
                  <Skeleton height={24} width="50%" />
                  <Skeleton
                    height={34}
                    sx={{ width: { xs: '100%', md: '60%' } }}
                  />
                </>
              ) : (
                <Typography variant="h6" fontWeight={700}>
                  {availabilitySummary}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {showIntegrationBox && (
          <Grid size={{ xs: 12, lg: 4 }}>
          <Card
            sx={{
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
              minHeight: '100%',
            }}
          >
            <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
                <Hub sx={{ color: 'primary.main' }} />
                <Typography variant="h6">
                  {t('pages:integrations.contactZ.overview.title')}
                </Typography>
              </Box>

              {loading ? (
                <>
                  <Skeleton height={28} />
                  <Skeleton height={22} />
                  <Skeleton height={22} />
                  <Skeleton height={120} />
                </>
              ) : integration && canViewIntegrationBinding ? (
                <>
                  <Stack spacing={1}>
                    <Typography variant="body2" color="text.secondary">
                      {t('pages:integrations.contactZ.overview.organizationUuid')}
                    </Typography>
                    <Typography sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                      {integration.organizationUuid}
                    </Typography>
                  </Stack>
                  <Stack spacing={1}>
                    <Typography variant="body2" color="text.secondary">
                      {t('pages:integrations.contactZ.overview.integrationConfigUuid')}
                    </Typography>
                    <Typography sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                      {integration.integrationConfigUuid}
                    </Typography>
                  </Stack>
                  <Stack spacing={1}>
                    <Typography variant="body2" color="text.secondary">
                      {t('pages:integrations.contactZ.overview.apiKey')}
                    </Typography>
                    <Typography sx={{ fontFamily: 'monospace' }}>
                      {integration.maskedApiKey}
                    </Typography>
                  </Stack>
                  <Stack spacing={1}>
                    <Typography variant="body2" color="text.secondary">
                      {t('pages:integrations.contactZ.overview.webhook')}
                    </Typography>
                    <Typography sx={{ wordBreak: 'break-all' }}>
                      {integration.webhookEndpoint}
                    </Typography>
                  </Stack>
                  <Stack spacing={1}>
                    <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.75 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('pages:integrations.contactZ.overview.eventTypes')}
                      </Typography>
                      <Tooltip title={t('pages:integrations.contactZ.overview.eventTypesHelp')} arrow>
                        <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                      </Tooltip>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {integration.eventTypes.map((eventType) => (
                        <Chip key={eventType} label={eventType} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Stack>
                  <Divider />
                  <Stack spacing={1}>
                    <Typography variant="body2" color="text.secondary">
                      {t('pages:integrations.contactZ.overview.lastBootstrappedAt')}
                    </Typography>
                    <Typography>
                      {formatDateTime(integration.lastBootstrappedAt, {
                        fallback: t('pages:integrations.contactZ.overview.notAvailable'),
                        locale,
                      })}
                    </Typography>
                  </Stack>
                  <Stack spacing={1}>
                    <Typography variant="body2" color="text.secondary">
                      {t('pages:integrations.contactZ.overview.lastSyncAt')}
                    </Typography>
                    <Typography>
                      {formatDateTime(integration.lastSyncAt, {
                        fallback: t('pages:integrations.contactZ.overview.notAvailable'),
                        locale,
                      })}
                    </Typography>
                  </Stack>
                  {integration.lastError && (
                    <Alert severity="warning">
                      {integration.lastError}
                    </Alert>
                  )}
                  <Divider />
                  <Stack spacing={1.5}>
                    <TextField
                      label={t('pages:integrations.contactZ.actions.testWebhookLabel')}
                      value={testWebhookMessage}
                      onChange={(event) => setTestWebhookMessage(event.target.value)}
                      size="small"
                    />
                    <Button
                      startIcon={busyAction === 'test-webhook' ? <CircularProgress size={16} color="inherit" /> : <Chat />}
                      variant="outlined"
                      onClick={handleTestWebhook}
                    >
                      {t('pages:integrations.contactZ.actions.testWebhook')}
                    </Button>
                    {canManualBind && (
                      <Button
                        startIcon={busyAction === 'manual-bind' ? <CircularProgress size={16} color="inherit" /> : <Hub />}
                        variant="outlined"
                        onClick={handleOpenManualBindDialog}
                      >
                        {t('pages:integrations.contactZ.actions.manualBind')}
                      </Button>
                    )}
                  </Stack>
                </>
              ) : integration ? (
                <>
                  <Alert severity="success">
                    {t('pages:integrations.contactZ.overview.bindingConfiguredRestricted')}
                  </Alert>
                  {canManualBind && (
                    <Button
                      startIcon={busyAction === 'manual-bind' ? <CircularProgress size={16} color="inherit" /> : <Hub />}
                      variant="outlined"
                      onClick={handleOpenManualBindDialog}
                    >
                      {t('pages:integrations.contactZ.actions.manualBind')}
                    </Button>
                  )}
                </>
              ) : (
                <Stack spacing={2}>
                  <Alert severity="info">
                    {t('pages:integrations.contactZ.overview.notBootstrapped')}
                  </Alert>
                  {canManualBind && (
                    <Button
                      startIcon={busyAction === 'manual-bind' ? <CircularProgress size={16} color="inherit" /> : <Hub />}
                      variant="outlined"
                      onClick={handleOpenManualBindDialog}
                    >
                      {t('pages:integrations.contactZ.actions.manualBind')}
                    </Button>
                  )}
                </Stack>
              )}
            </CardContent>
          </Card>
          </Grid>
        )}

        <Grid size={{ xs: 12, lg: showIntegrationBox ? 8 : 12 }}>
          <Card
            sx={{
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
            }}
          >
            <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <Box
                sx={{
                  alignItems: { xs: 'stretch', sm: 'center' },
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2,
                  justifyContent: 'space-between',
                }}
              >
                <Box>
                  <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
                    <Wifi sx={{ color: 'primary.main' }} />
                    <Typography variant="h6">
                      {t('pages:integrations.contactZ.connections.title')}
                    </Typography>
                  </Box>
                  <Typography color="text.secondary" variant="body2">
                    {t('pages:integrations.contactZ.connections.subtitle')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Button
                    startIcon={<Refresh />}
                    variant="outlined"
                    onClick={() => void loadPage()}
                  >
                    {t('common:actions.refresh')}
                  </Button>
                  {canCreateConnections && (
                    <Button
                      startIcon={<Add />}
                      variant="contained"
                      onClick={handleOpenCreateDialog}
                      disabled={!integration}
                    >
                      {t('pages:integrations.contactZ.connections.actions.new')}
                    </Button>
                  )}
                </Box>
              </Box>

              {!integration && (
                <Alert severity="info">
                  {t('pages:integrations.contactZ.connections.bootstrapFirst')}
                </Alert>
              )}

              {loading ? (
                <Stack spacing={2}>
                  <Skeleton variant="rectangular" height={180} sx={{ borderRadius: 3 }} />
                  <Skeleton variant="rectangular" height={180} sx={{ borderRadius: 3 }} />
                </Stack>
              ) : (
                <Stack spacing={2}>
                  {connections.map((connection) => {
                    const qrPlaceholder = getQrPlaceholderCopy(t, connection.status);
                    const QrPlaceholderIcon = qrPlaceholder.icon;
                    const isConnectedConnection = connection.status === 'connected';
                    const canConnectConnection = canConnectContactZConnection(connection.status);
                    const canDisconnectConnection = canDisconnectContactZConnection(connection.status);

                    return (
                    <Card
                      key={connection.id}
                      sx={{
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                        boxShadow: 'none',
                      }}
                    >
                      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box
                          sx={{
                            alignItems: { xs: 'flex-start', md: 'center' },
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            gap: 1.5,
                            justifyContent: 'space-between',
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="h6" fontWeight={700}>
                              {connection.name}
                            </Typography>
                            <Typography color="text.secondary" variant="body2" sx={{ wordBreak: 'break-all' }}>
                              {connection.channelUuid}
                            </Typography>
                          </Box>
                          <Box sx={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            <ContactZStatusChip
                              status={connection.status}
                              label={t(`pages:integrations.contactZ.status.${connection.status}`)}
                            />
                          </Box>
                        </Box>

                        <Grid container spacing={2}>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <Stack spacing={1.25}>
                              <Box>
                                <Typography variant="body2" color="text.secondary">
                                  {t('pages:integrations.contactZ.connections.fields.phoneNumber')}
                                </Typography>
                                <Typography>
                                  {connection.phoneNumber
                                    ? formatPhone(connection.phoneNumber)
                                    : t('pages:integrations.contactZ.connections.notAvailable')}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="body2" color="text.secondary">
                                  {t('pages:integrations.contactZ.connections.fields.connectedAt')}
                                </Typography>
                                <Typography>
                                  {formatDateTime(connection.connectedAt, {
                                    fallback: t('pages:integrations.contactZ.connections.notAvailable'),
                                    locale,
                                  })}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="body2" color="text.secondary">
                                  {t('pages:integrations.contactZ.connections.fields.lastStatusAt')}
                                </Typography>
                                <Typography>
                                  {formatDateTime(connection.lastStatusAt, {
                                    fallback: t('pages:integrations.contactZ.connections.notAvailable'),
                                    locale,
                                  })}
                                </Typography>
                              </Box>
                            </Stack>
                          </Grid>
                          <Grid size={{ xs: 12, md: 6 }}>
                            {connection.status === 'connecting' && connection.lastGeneratedQrCode ? (
                              <Box
                                sx={{
                                  alignItems: 'center',
                                  border: `1px dashed ${alpha(theme.palette.primary.main, 0.35)}`,
                                  borderRadius: 3,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 1,
                                  justifyContent: 'center',
                                  minHeight: 220,
                                  p: 2,
                                }}
                              >
                                <QrCode2 sx={{ color: 'primary.main' }} />
                                <Typography fontWeight={600}>
                                  {t('pages:integrations.contactZ.connections.qr.title')}
                                </Typography>
                                <Box
                                  component="img"
                                  src={connection.lastGeneratedQrCode}
                                  alt={t('pages:integrations.contactZ.connections.qr.alt', { name: connection.name })}
                                  sx={{ backgroundColor: '#fff', borderRadius: 2, maxHeight: 160, maxWidth: '100%', p: 1 }}
                                />
                              </Box>
                            ) : (
                              <Box
                                sx={{
                                  alignItems: 'center',
                                  border: `1px dashed ${alpha(theme.palette.divider, 0.9)}`,
                                  borderRadius: 3,
                                  color: 'text.secondary',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 1,
                                  justifyContent: 'center',
                                  minHeight: 220,
                                  p: 2,
                                }}
                              >
                                <QrPlaceholderIcon
                                  sx={{
                                    ...(qrPlaceholder.iconColor ? { color: qrPlaceholder.iconColor } : {}),
                                    ...(qrPlaceholder.iconSize ? { fontSize: qrPlaceholder.iconSize } : {}),
                                  }}
                                />
                                <Typography variant="body2" align="center">
                                  {qrPlaceholder.message}
                                </Typography>
                              </Box>
                            )}
                          </Grid>
                        </Grid>

                        <Divider />

                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {canControlConnections && (
                            <>
                              {isConnectedConnection && (
                                <Button
                                  size="small"
                                  startIcon={busyAction === `sync-${connection.id}` ? <CircularProgress size={14} color="inherit" /> : <Autorenew />}
                                  variant="outlined"
                                  disabled={busyAction !== null}
                                  onClick={() => void handleConnectionAction(connection, 'sync')}
                                >
                                  {t('pages:integrations.contactZ.connections.actions.sync')}
                                </Button>
                              )}
                              {canConnectConnection && (
                                <Button
                                  size="small"
                                  startIcon={busyAction === `connect-${connection.id}` ? <CircularProgress size={14} color="inherit" /> : <QrCode2 />}
                                  variant="outlined"
                                  disabled={busyAction !== null}
                                  onClick={() => void handleConnectionAction(connection, 'connect')}
                                >
                                  {t('pages:integrations.contactZ.connections.actions.connect')}
                                </Button>
                              )}
                              {canDisconnectConnection && (
                                <Button
                                  size="small"
                                  startIcon={busyAction === `disconnect-${connection.id}` ? <CircularProgress size={14} color="inherit" /> : <PowerSettingsNew />}
                                  variant="outlined"
                                  color="warning"
                                  disabled={busyAction !== null}
                                  onClick={() => void handleConnectionAction(connection, 'disconnect')}
                                >
                                  {t('pages:integrations.contactZ.connections.actions.disconnect')}
                                </Button>
                              )}
                              {isConnectedConnection && (
                                <Button
                                  size="small"
                                  startIcon={busyAction === `restart-${connection.id}` ? <CircularProgress size={14} color="inherit" /> : <RestartAlt />}
                                  variant="outlined"
                                  disabled={busyAction !== null}
                                  onClick={() => void handleConnectionAction(connection, 'restart')}
                                >
                                  {t('pages:integrations.contactZ.connections.actions.restart')}
                                </Button>
                              )}
                            </>
                          )}
                          {canUpdateConnections && (
                            <Tooltip title={t('common:actions.edit')}>
                              <IconButton size="small" onClick={() => handleOpenEditDialog(connection)}>
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canDeleteConnections && (
                            <Tooltip title={t('common:actions.delete')}>
                              <IconButton
                                color="error"
                                size="small"
                                onClick={() => void handleDeleteConnection(connection)}
                              >
                                <DeleteOutline fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                    );
                  })}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog
        open={dialogState.open}
        onClose={handleCloseDialog}
        fullScreen={fullScreenDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            {dialogState.mode === 'create'
              ? <Add sx={{ color: 'primary.main' }} />
              : <Edit sx={{ color: 'primary.main' }} />}
            <Box component="span">
              {dialogState.mode === 'create'
                ? t('pages:integrations.contactZ.connections.dialog.createTitle')
                : t('pages:integrations.contactZ.connections.dialog.editTitle')}
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label={t('pages:integrations.contactZ.connections.dialog.nameLabel')}
            margin="dense"
            value={dialogState.name}
            onChange={(event) => setDialogState((previous) => ({ ...previous, name: event.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            onClick={() => void handleSaveDialog()}
            variant="contained"
            startIcon={dialogState.mode === 'create' ? <Add /> : <Edit />}
          >
            {dialogState.mode === 'create'
              ? t('pages:integrations.contactZ.connections.dialog.createAction')
              : t('common:actions.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={manualBindDialog.open}
        onClose={handleCloseManualBindDialog}
        fullScreen={fullScreenDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Hub sx={{ color: 'primary.main' }} />
            <Box component="span">
              {t('pages:integrations.contactZ.manualBind.dialog.title')}
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Alert severity="info">
              {t('pages:integrations.contactZ.manualBind.dialog.description')}
            </Alert>
            {integration && connections.length > 0 && (
              <Alert severity="warning">
                {t('pages:integrations.contactZ.manualBind.dialog.replaceWarning', {
                  count: connections.length,
                })}
              </Alert>
            )}
            <TextField
              autoFocus
              fullWidth
              label={t('pages:integrations.contactZ.manualBind.dialog.organizationUuid')}
              value={manualBindDialog.organizationUuid}
              onChange={(event) => setManualBindDialog((previous) => ({
                ...previous,
                organizationUuid: event.target.value,
              }))}
            />
            <TextField
              fullWidth
              label={t('pages:integrations.contactZ.manualBind.dialog.integrationConfigUuid')}
              value={manualBindDialog.integrationConfigUuid}
              onChange={(event) => setManualBindDialog((previous) => ({
                ...previous,
                integrationConfigUuid: event.target.value,
              }))}
            />
            <TextField
              fullWidth
              label={t('pages:integrations.contactZ.manualBind.dialog.apiKey')}
              type="password"
              value={manualBindDialog.apiKey}
              onChange={(event) => setManualBindDialog((previous) => ({
                ...previous,
                apiKey: event.target.value,
              }))}
            />
            <FormControlLabel
              control={(
                <Checkbox
                  checked={manualBindDialog.importExistingConnections}
                  onChange={(event) => setManualBindDialog((previous) => ({
                    ...previous,
                    importExistingConnections: event.target.checked,
                  }))}
                />
              )}
              label={t('pages:integrations.contactZ.manualBind.dialog.importExistingConnections')}
            />
            <FormControlLabel
              control={(
                <Checkbox
                  checked={manualBindDialog.forceReplace}
                  onChange={(event) => setManualBindDialog((previous) => ({
                    ...previous,
                    forceReplace: event.target.checked,
                  }))}
                />
              )}
              label={t('pages:integrations.contactZ.manualBind.dialog.forceReplace')}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseManualBindDialog}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            onClick={() => void handleSaveManualBindDialog()}
            variant="contained"
            startIcon={<Hub />}
          >
            {t('pages:integrations.contactZ.manualBind.dialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {DialogComponent}
    </Box>
  );
}
